import { createSlice, createAsyncThunk, createAction } from '@reduxjs/toolkit';
import { Cookies } from 'react-cookie';

import { get, MICROSERVICES } from 'api';
import { history, store } from 'store';
import { decodeToken } from 'utils/helpers';
import { IMyKnownError, REQUEST_LIFE_CICLE } from 'interfaces/requests';
import { ITokenParsed } from 'interfaces/User';
import { closeAlert, openAlert } from 'modules/alertHandler';
import { setSecLevelAction } from 'modules/secLevel/actions';

import endpoints from './endpoints';
import { IUserState, ILoginActionPayload } from './interfaces';
import {
  getParsedPermissions,
  userLoginActionFailedMessageByError,
} from './helpers';
import { setMainNavigationContainerByUserPermission } from 'utils/navigationContainers';
import { removeJWTCookie } from 'utils/cookies';

export const userLogout = createAction('USER_LOGOUT');

export const userLogoutAction = createAsyncThunk<void, void>(
  'logout',
  (_, { dispatch }) => {
    removeJWTCookie();
    sessionStorage.clear();
    dispatch(closeAlert());
    dispatch(userLogout());
    dispatch(clearUser());
    history.push('/');
  },
);

export const openAlertToUserLogoutAction = createAsyncThunk<void, void>(
  'openAlertLogout',
  (_, { dispatch }) => {
    dispatch(userLogoutAction());
    dispatch(
      openAlert({
        title: 'Sistema Indisponível',
        content:
          'Lamentamos muito mas o sistema está indisponível, favor tentar novamente',
      }),
    );
  },
);

export const userLoginAction = createAsyncThunk<void, ILoginActionPayload>(
  'userLogin',
  async ({ user, password }, { dispatch, rejectWithValue }) => {
    try {
      await get(MICROSERVICES.AUTH, {
        url: endpoints.login(),
        headers: { user, password },
      });
      const cookies = new Cookies();
      const token = cookies.get('jwt');
      await dispatch(setUserDataAction(token));
    } catch (error) {
      console.error('error at userLogin', error);
      dispatch(
        openAlert(
          userLoginActionFailedMessageByError(
            String(error.response ? error.response.status : 'without-response'),
          ),
        ),
      );
      return rejectWithValue({ message: 'Token inválido' });
    }
  },
);

export const setUserDataAction = createAsyncThunk<
  ITokenParsed,
  string,
  { rejectValue: IMyKnownError }
>('setUserData', async (token, { dispatch, rejectWithValue }) => {
  try {
    const oldPermissions = store.getState().user?.permissions;
    const tokenDecoded = decodeToken(token);
    const tokenParsed: ITokenParsed = {
      ...tokenDecoded,
      permissions: getParsedPermissions(tokenDecoded.permissions),
    };

    const isSamePermission =
      JSON.stringify(oldPermissions) ===
      JSON.stringify(tokenParsed.permissions);

    if (!isSamePermission || !window.location.pathname.match('home')) {
      dispatch(setSecLevelAction(1));
      setMainNavigationContainerByUserPermission(tokenParsed.permissions);
      history.push('/home');
    }
    return tokenParsed;
  } catch (error) {
    console.error('error at setUserDataAction', error);
    dispatch(
      openAlert({
        title: 'Erro!',
        content: 'Token inválido',
      }),
    );
    return rejectWithValue({ message: 'Token inválido' });
  }
});

const initialState: IUserState = {
  errorMessage: '',
  loading: REQUEST_LIFE_CICLE.IDLE,
  requesting: REQUEST_LIFE_CICLE.IDLE,
  token: '',
  name: '',
  user: '',
  permissions: {},
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearUser: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(setUserDataAction.fulfilled, (state, action) => {
      state.token = action.payload.token;
      state.name = action.payload.name;
      state.user = action.payload.user;
      state.permissions = action.payload.permissions;
      state.loading = REQUEST_LIFE_CICLE.SUCCEEDED;
    });
    builder.addCase(setUserDataAction.pending, (state) => {
      state.loading = REQUEST_LIFE_CICLE.PENDING;
    });
    builder.addCase(setUserDataAction.rejected, (state) => {
      state.loading = REQUEST_LIFE_CICLE.FAILED;
    });
    builder.addCase(userLoginAction.fulfilled, (state) => {
      state.requesting = REQUEST_LIFE_CICLE.SUCCEEDED;
    });
    builder.addCase(userLoginAction.pending, (state) => {
      state.requesting = REQUEST_LIFE_CICLE.PENDING;
    });
    builder.addCase(userLoginAction.rejected, (state) => {
      state.requesting = REQUEST_LIFE_CICLE.FAILED;
    });
  },
});

const { reducer, actions } = userSlice;
export const { clearUser } = actions;
export default reducer;
